import type {
  AuditActorType,
  AuditLogFilters,
  AuditLogListItem,
  AuditOutcome,
} from '../../types/audit-log.types';

/**
 * Curated catalog of action keys grouped by domain prefix.
 * Mirrors the @Audit() decorator usage in agentic-identity-service.
 * Add new actions here as the backend grows.
 */
export const AUDIT_ACTION_GROUPS: Record<string, string[]> = {
  auth: [
    'auth.login',
    'auth.logout',
    'auth.logout_all',
    'auth.register',
    'auth.password.change',
    'auth.oauth.exchange_code',
    'auth.impersonate.start',
    'auth.impersonate.stop',
    'auth.session.terminate',
    'auth.sessions.list',
    'auth.subsidiary.switch',
  ],
  user: ['user.create', 'user.update', 'user.delete', 'user.invite'],
  subsidiary: [
    'subsidiary.create',
    'subsidiary.update',
    'subsidiary.delete',
    'subsidiary.export',
    'subsidiary.import',
    'subsidiary.member.add',
    'subsidiary.member.bulk_add',
    'subsidiary.member.invite',
    'subsidiary.member.remove',
    'subsidiary.member.update',
  ],
  role: ['role.create', 'role.update', 'role.delete', 'role.permissions.replace'],
  permission: [
    'permission.create',
    'permission.update',
    'permission.delete',
    'permission.toggle',
    'permission.bulk_create',
  ],
  resource: ['resource.create', 'resource.update', 'resource.delete', 'resource.toggle'],
  screen: [
    'screen.create',
    'screen.update',
    'screen.delete',
    'screen.toggle',
    'screen.bulk_sort',
    'screen.subsidiary.assign',
    'screen.subsidiary.update',
    'screen.subsidiary.remove',
    'screen.subsidiary.toggle',
    'screen.subsidiary.bulk_update',
  ],
  abac: [
    'abac.policy.create',
    'abac.policy.update',
    'abac.policy.delete',
    'abac.user_attributes.set',
    'abac.user_attributes.remove',
    'abac.resource_attributes.set',
    'abac.resource_attributes.remove',
    'abac.evaluate',
  ],
  sso: [
    'sso.saml.configure',
    'sso.saml.update',
    'sso.oidc.configure',
    'sso.oidc.update',
    'sso.oauth_domains.configure',
    'sso.oauth_domains.update',
    'sso.disable',
    'sso.delete',
    'sso.subsidiary.saml.configure',
    'sso.subsidiary.saml.update',
    'sso.subsidiary.oidc.configure',
    'sso.subsidiary.oidc.update',
    'sso.subsidiary.oauth_domains.configure',
    'sso.subsidiary.oauth_domains.update',
    'sso.subsidiary.disable',
    'sso.subsidiary.delete',
  ],
  service_user: [
    'service_user.create',
    'service_user.update',
    'service_user.delete',
    'service_user.kill',
    'service_user.credential.issue',
    'service_user.credential.rotate',
    'service_user.credential.revoke',
  ],
  oauth: ['oauth.token.issue', 'oauth.token.revoke'],
  feature_flag: [
    'feature_flag.create',
    'feature_flag.update',
    'feature_flag.delete',
    'feature_flag.override.upsert',
    'feature_flag.override.remove',
  ],
  casbin: ['casbin.regenerate'],
  organization: ['organization.create', 'organization.update', 'organization.delete'],
};

/** Resource enum values offered by the resource filter dropdown. */
export const AUDIT_RESOURCE_OPTIONS = [
  'organization',
  'subsidiary',
  'user',
  'role',
  'permission',
  'resource',
  'screen',
  'abac_policy',
  'abac_attribute',
  'sso_config',
  'service_user',
  'service_user_credential',
  'oauth_token',
  'feature_flag',
  'feature_flag_override',
  'session',
  'casbin_policy',
] as const;

/** Actor-type pill options shown in the filter bar. */
export const ACTOR_TYPE_OPTIONS: { value: AuditActorType; label: string }[] = [
  { value: 'USER', label: 'User' },
  { value: 'SERVICE_USER', label: 'Service' },
  { value: 'ANONYMOUS', label: 'Anonymous' },
];

/** Outcome pill options shown in the filter bar. */
export const OUTCOME_OPTIONS: { value: AuditOutcome; label: string }[] = [
  { value: 'SUCCESS', label: 'Success' },
  { value: 'FAILURE', label: 'Failure' },
];

/** Returns the leading domain segment of a dotted action key (e.g. `auth.login` → `auth`). */
export function actionDomain(action: string): string {
  const idx = action.indexOf('.');
  return idx === -1 ? action : action.slice(0, idx);
}

const DOMAIN_COLORS: Record<string, string> = {
  auth: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  user: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  subsidiary: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  role: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  permission: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  resource: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  screen: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  abac: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  sso: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  service_user: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
  oauth: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300',
  feature_flag: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  casbin: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  organization: 'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-300',
};

/** Maps a domain name to a Tailwind class string for the action badge. */
export function actionDomainColor(domain: string): string {
  return DOMAIN_COLORS[domain] ?? 'bg-muted text-muted-foreground';
}

/** Builds a human-friendly label for the row's actor (user, service, or anonymous). */
export function formatActorLabel(item: AuditLogListItem): string {
  if (item.actorType === 'ANONYMOUS') return 'Anonymous';
  if (item.actorType === 'SERVICE_USER') {
    return item.user?.email ? `Service · ${item.user.email}` : 'Service user';
  }
  if (!item.user) return item.userId ? 'User (deleted)' : 'Unknown user';
  const name = [item.user.firstName, item.user.lastName].filter(Boolean).join(' ');
  return name || item.user.email;
}

/** A single row in the diff table — one changed field. */
export interface DiffRow {
  field: string;
  before: unknown;
  after: unknown;
}

/** Pairs `before` and `after` snapshots into per-field diff rows for the changes tab. */
export function buildDiffRows(
  before: unknown,
  after: unknown,
  changedFields?: string[],
): DiffRow[] {
  if (!changedFields || changedFields.length === 0) return [];
  const beforeObj: Record<string, unknown> =
    before && typeof before === 'object' ? (before as Record<string, unknown>) : {};
  const afterObj: Record<string, unknown> =
    after && typeof after === 'object' ? (after as Record<string, unknown>) : {};
  return changedFields.map((field) => ({
    field,
    before: beforeObj[field],
    after: afterObj[field],
  }));
}

const PRESET_24H = 24 * 60 * 60 * 1000;
const PRESET_1H = 60 * 60 * 1000;
const PRESET_7D = 7 * PRESET_24H;
const PRESET_30D = 30 * PRESET_24H;

/** Date-range preset options understood by the filter bar. */
export type DateRangePreset = '1h' | '24h' | '7d' | '30d' | 'custom';

/** Display options for the date-range select. */
export const DATE_PRESET_OPTIONS: { value: DateRangePreset; label: string }[] = [
  { value: '1h', label: 'Last 1 hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom range' },
];

/** Converts a preset key into an ISO `from` timestamp (undefined for `custom`). */
export function presetToFromIso(preset: DateRangePreset, now = Date.now()): string | undefined {
  switch (preset) {
    case '1h':
      return new Date(now - PRESET_1H).toISOString();
    case '24h':
      return new Date(now - PRESET_24H).toISOString();
    case '7d':
      return new Date(now - PRESET_7D).toISOString();
    case '30d':
      return new Date(now - PRESET_30D).toISOString();
    case 'custom':
      return undefined;
  }
}

/** Initial filter state used when the URL has no filter params. */
export function defaultFilters(): AuditLogFilters {
  const from = presetToFromIso('24h');
  return {
    page: 1,
    pageSize: 25,
    ...(from ? { from } : {}),
  };
}

/** Convert filter object into URL search-param-friendly shape (strings only). */
export function filtersToSearchParams(
  filters: AuditLogFilters,
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = String(value);
  }
  return out;
}

/** Inverse of `filtersToSearchParams` — reads the URL search record into filter state. */
export function searchParamsToFilters(search: Record<string, unknown>): AuditLogFilters {
  const f: AuditLogFilters = {};
  if (typeof search.page === 'string') f.page = Number(search.page) || 1;
  if (typeof search.pageSize === 'string') f.pageSize = Number(search.pageSize) || 25;
  if (typeof search.from === 'string') f.from = search.from;
  if (typeof search.to === 'string') f.to = search.to;
  if (typeof search.action === 'string') f.action = search.action;
  if (typeof search.actionPrefix === 'string') f.actionPrefix = search.actionPrefix;
  if (typeof search.resource === 'string') f.resource = search.resource;
  if (typeof search.resourceId === 'string') f.resourceId = search.resourceId;
  if (typeof search.actorType === 'string') f.actorType = search.actorType as AuditActorType;
  if (typeof search.outcome === 'string') f.outcome = search.outcome as AuditOutcome;
  if (typeof search.userId === 'string') f.userId = search.userId;
  if (typeof search.subsidiaryId === 'string') f.subsidiaryId = search.subsidiaryId;
  if (typeof search.requestId === 'string') f.requestId = search.requestId;
  if (typeof search.q === 'string') f.q = search.q;
  return f;
}

/** Returns true when at least one user-facing filter (besides defaults) is set. */
export function isFilterActive(filters: AuditLogFilters): boolean {
  const ignored = new Set(['page', 'pageSize', 'from']);
  for (const [key, value] of Object.entries(filters)) {
    if (ignored.has(key)) continue;
    if (value !== undefined && value !== '') return true;
  }
  return false;
}

/** CSV export column descriptor (kept for future export work; not wired in v1). */
export function buildExportColumns(): {
  key: keyof AuditLogListItem | 'actor';
  header: string;
}[] {
  return [
    { key: 'createdAt', header: 'Timestamp' },
    { key: 'actor', header: 'Actor' },
    { key: 'actorType', header: 'Actor Type' },
    { key: 'action', header: 'Action' },
    { key: 'resource', header: 'Resource' },
    { key: 'resourceId', header: 'Resource ID' },
    { key: 'outcome', header: 'Outcome' },
    { key: 'errorCode', header: 'Error' },
    { key: 'method', header: 'Method' },
    { key: 'path', header: 'Path' },
    { key: 'ipAddress', header: 'IP' },
    { key: 'requestId', header: 'Request ID' },
  ];
}
